import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PLACEHOLDER_IMAGES } from '@/shared/constants/placeholders';

interface ConversationOfferBannerProps {
  offerId: string;
  offerTitle: string;
  offerPhotoUrl?: string | null;
  offerStatus?: string;
}

export function ConversationOfferBanner({
  offerId,
  offerTitle,
  offerPhotoUrl,
  offerStatus,
}: Readonly<ConversationOfferBannerProps>) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <Box
      onClick={() => navigate(`/offers/${offerId}`)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1.5,
        bgcolor: 'grey.100',
        borderBottom: '1px solid',
        borderColor: 'divider',
        cursor: 'pointer',
        '&:hover': { bgcolor: 'grey.200' },
      }}
      role="link"
      aria-label={t('messages.viewOffer')}
      data-testid="conversation-offer-banner"
    >
      <Box
        component="img"
        src={offerPhotoUrl || PLACEHOLDER_IMAGES.product}
        alt={offerTitle}
        onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
          e.currentTarget.src = PLACEHOLDER_IMAGES.product;
        }}
        sx={{
          width: 48,
          height: 48,
          borderRadius: 1,
          objectFit: 'cover',
          flexShrink: 0,
          bgcolor: 'grey.300',
        }}
      />
      <Box sx={{ overflow: 'hidden', minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {offerTitle}
        </Typography>
        {offerStatus === 'CLOSED' && (
          <Typography
            variant="caption"
            sx={{ color: 'error.main', fontWeight: 500, lineHeight: 1.3 }}
          >
            {t('messages.offerClosed')}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
